import React, { useCallback, useState } from 'react';
import './ChecklistIntro.scss';
import { useTranslation } from 'react-i18next';
import { ChecklistTab } from '../data/checklistData';
import TrafficChart from './TrafficChart';

interface ChecklistIntroProps {
  tab: ChecklistTab;
}

const SCROLL_OFFSET = 80;

const ChecklistIntro: React.FC<ChecklistIntroProps> = ({ tab }) => {
  const { t } = useTranslation();

  const [takeawaysOpen, setTakeawaysOpen] = useState(false);

  const hasKeyTakeaways = tab.introKeyTakeaways && tab.introKeyTakeaways.length > 0;
  const hasBullets = tab.introBulletKeys.length > 0;

  const handleTakeawayClick = useCallback((sectionId?: string) => {
    if (!sectionId) return;
    const el = document.getElementById(`section-${sectionId}`);
    if (!el) return;
    const scrollContainer = el.closest('.layout__right');
    if (scrollContainer) {
      const top = el.getBoundingClientRect().top - scrollContainer.getBoundingClientRect().top + scrollContainer.scrollTop - SCROLL_OFFSET;
      scrollContainer.scrollTo({ top, behavior: 'smooth' });
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  return (
    <div className="checklist-intro">
      <p className="checklist-intro__text">{t(tab.introDescKey)}</p>

        {hasKeyTakeaways && (
          <div className="checklist-card checklist-intro__takeaways-box">
            <button
              type="button"
              className="checklist-intro__takeaways-header"
              aria-expanded={takeawaysOpen}
              onClick={() => setTakeawaysOpen((prev) => !prev)}
            >
              <span className="material-symbols-outlined checklist-intro__takeaways-icon">lightbulb</span>
              {t(tab.introHelperKey)}
              <span
                className={`ico--bottom ico--xs checklist-intro__takeaways-chevron${takeawaysOpen ? ' checklist-intro__takeaways-chevron--open' : ''}`}
              />
            </button>
            {takeawaysOpen && (
              <div className="checklist-intro__takeaways-grid">
                {tab.introKeyTakeaways!.map((kt, idx) => (
                  <div
                    key={kt.titleKey}
                    className={`checklist-intro__takeaway-card${kt.sectionId ? ' checklist-intro__takeaway-card--clickable' : ''}`}
                    role={kt.sectionId ? 'button' : undefined}
                    tabIndex={kt.sectionId ? 0 : undefined}
                    onClick={() => handleTakeawayClick(kt.sectionId)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleTakeawayClick(kt.sectionId); }}
                  >
                    <span className="checklist-intro__takeaway-number">{idx + 1}</span>
                    <div>
                      <p className="checklist-intro__takeaway-title">{t(kt.titleKey)}</p>
                      <p className="checklist-intro__takeaway-desc">{t(kt.descriptionKey)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!hasKeyTakeaways && (
          <>
            <p className="checklist-intro__text">{t(tab.introHelperKey)}</p>
            {hasBullets && (
              <ul className="checklist-intro__list">
                {tab.introBulletKeys.map((key) => (
                  <li key={key}>{t(key)}</li>
                ))}
              </ul>
            )}
          </>
        )}

        <p className="checklist-intro__text checklist-intro__text--footer">{t(tab.introFooterKey)}</p>

        {tab.showChart && <TrafficChart />}

        {tab.introFooter2Key && (
          <p className="checklist-intro__chart-caption">{t(tab.introFooter2Key)}</p>
        )}
    </div>
  );
};

export { ChecklistIntro };
export default ChecklistIntro;
